/**
 * Reset Password page — Two-step password reset flow.
 *
 * Step 1: Email entry — user enters email to receive a reset code.
 *         Security: always shows the same success transition regardless of whether
 *         the email is registered (account enumeration prevention, BR-001.1).
 *
 * Step 2: Code + new password entry — user enters reset code and new password.
 *         On success: shows success state with Sign In button (no auto-sign-in, BR-002.2).
 *         On invalid/expired code: shows error banner with Resend Code link (returns to Step 1).
 *
 * Both steps live in this component; step state controls which form is rendered.
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, Mail, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { PasswordRequirements } from '@/components/PasswordRequirements';
import { forgotPassword, confirmPasswordReset } from '@/services/authApi';
import { validatePassword } from '@/lib/passwordValidator';
import type { AuthError } from '@/types/auth';

export function ResetPasswordPage() {
  const navigate = useNavigate();

  // Step control — 1: email entry, 2: code + password entry
  const [step, setStep] = useState<1 | 2>(1);

  // Step 1 state
  const [email, setEmail] = useState('');

  // Step 2 state
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Shared state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  // Inline field-level errors for Step 2
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);

  // Derive password validity for submit button disabled logic
  const { isValid: isPasswordValid } = validatePassword(newPassword);

  // ── Step 1 helpers ─────────────────────────────────────────────────────────

  /**
   * Basic email format check: must contain '@' and a dot in the domain part.
   */
  const validateEmail = (val: string): boolean =>
    val.includes('@') && !!val.split('@')[1]?.includes('.');

  /**
   * Run format validation on blur if the field is non-empty.
   */
  const handleEmailBlur = () => {
    if (email && !validateEmail(email)) {
      setError('Please enter a valid email address.');
    } else if (email) {
      setError(null);
    }
  };

  const handleStep1Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Email address is required.');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsLoading(true);

    try {
      await forgotPassword({ email: email.trim() });
      setStep(2);
    } catch (err) {
      // Never reveal specifics — always show the generic retry message
      const _authError = err as AuthError;
      void _authError;
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Step 2 helpers ─────────────────────────────────────────────────────────

  /**
   * Validate password on blur: set per-rule error message if invalid.
   */
  const handlePasswordBlur = () => {
    if (!newPassword) return;
    const { isValid, requirements } = validatePassword(newPassword);
    if (!isValid) {
      const firstUnmet = requirements.find((r) => !r.met);
      setPasswordError(firstUnmet ? firstUnmet.message : 'Password does not meet requirements.');
    } else {
      setPasswordError(null);
    }
  };

  /**
   * Validate confirm password on blur: set mismatch error if values differ.
   */
  const handleConfirmPasswordBlur = () => {
    if (!confirmPassword) return;
    if (confirmPassword !== newPassword) {
      setConfirmPasswordError('Passwords do not match.');
    } else {
      setConfirmPasswordError(null);
    }
  };

  const handleStep2Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setPasswordError(null);
    setConfirmPasswordError(null);

    // Client-side validation before API call
    const { isValid, requirements } = validatePassword(newPassword);
    if (!isValid) {
      const firstUnmet = requirements.find((r) => !r.met);
      setPasswordError(firstUnmet ? firstUnmet.message : 'Password does not meet requirements.');
      return;
    }

    if (confirmPassword !== newPassword) {
      setConfirmPasswordError('Passwords do not match.');
      return;
    }

    setIsLoading(true);

    try {
      await confirmPasswordReset({
        email: email.trim(),
        code: resetCode.trim(),
        newPassword,
      });
      setIsSuccess(true);
    } catch (err) {
      const authError = err as AuthError;
      if (authError.code === 'INVALID_OR_EXPIRED_CODE') {
        setError('The reset code is invalid or has expired. Please request a new code.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Resend: reset to Step 1 keeping email pre-filled so user does not have to
   * retype it. Clears all Step 2 fields and errors.
   */
  const handleResend = () => {
    setStep(1);
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
    setPasswordError(null);
    setConfirmPasswordError(null);
  };

  // ── Submit button disabled logic ────────────────────────────────────────────

  const isStep2SubmitDisabled =
    isLoading ||
    !resetCode.trim() ||
    !newPassword ||
    !confirmPassword ||
    !isPasswordValid ||
    !!passwordError ||
    !!confirmPasswordError;

  // ── Success state (replaces entire page) ───────────────────────────────────

  if (isSuccess) {
    return (
      <div
        className="flex min-h-screen items-center justify-center px-3 md:px-4 py-8 md:py-12"
        style={{ backgroundColor: '#fdf8f0' }}
      >
        <Card className="w-full max-w-md bg-otter-paper rounded-[20px] shadow-md border-[#E7D8C8]">
          <CardHeader className="space-y-1 text-center">
            <CheckCircle className="mx-auto h-12 w-12 text-otter-orange-500" />
            <CardTitle className="text-2xl font-bold text-otter-earth-800">
              Password Reset Successful!
            </CardTitle>
            <CardDescription className="text-otter-earth-700">
              Your password has been reset successfully!
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex flex-col space-y-4">
            <Button
              variant="warm"
              className="w-full rounded-[10px]"
              onClick={() => navigate('/login')}
            >
              Sign In
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // ── Step 2: Code + New Password entry ──────────────────────────────────────

  if (step === 2) {
    return (
      <div
        className="flex min-h-screen items-center justify-center px-3 md:px-4 py-8 md:py-12"
        style={{ backgroundColor: '#fdf8f0' }}
      >
        <Card className="w-full max-w-md bg-otter-paper rounded-[20px] shadow-md border-[#E7D8C8]">
          <CardHeader className="space-y-1 text-center">
            <Mail className="mx-auto h-12 w-12 text-otter-orange-500" />
            <CardTitle className="text-2xl font-bold text-otter-earth-800">
              Enter Reset Code
            </CardTitle>
            <CardDescription className="text-otter-earth-700">
              A reset code has been sent to <strong>{email}</strong>. Please check your inbox.
            </CardDescription>
          </CardHeader>

          <form onSubmit={handleStep2Submit}>
            <CardContent className="space-y-4">
              {/* Error banner with Resend Code link */}
              {error && (
                <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
                  {error}{' '}
                  <button
                    type="button"
                    onClick={handleResend}
                    className="font-medium underline text-red-800 hover:text-red-600"
                  >
                    Resend Code
                  </button>
                </div>
              )}

              {/* Reset Code */}
              <div className="space-y-2">
                <Label htmlFor="resetCode" className="text-otter-earth-800">
                  Reset Code
                </Label>
                <Input
                  id="resetCode"
                  type="text"
                  placeholder="Enter your reset code"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value.replace(/\D/g, ''))}
                  disabled={isLoading}
                  className="h-11 md:h-10 border-[#E7D8C8] text-center text-lg tracking-widest"
                  autoComplete="one-time-code"
                  autoFocus
                />
              </div>

              {/* New Password */}
              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-otter-earth-800">
                  New Password
                </Label>
                <PasswordInput
                  id="newPassword"
                  placeholder="Enter your new password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    // Clear inline error on change so user gets live feedback
                    if (passwordError) setPasswordError(null);
                  }}
                  onBlur={handlePasswordBlur}
                  disabled={isLoading}
                  className="h-11 md:h-10 border-[#E7D8C8] focus-visible:ring-0 focus-visible:border-2 focus-visible:border-[#EA6A2A]"
                  autoComplete="new-password"
                />
                {/* Inline password error */}
                {passwordError && (
                  <p className="text-sm text-red-500 mt-1">{passwordError}</p>
                )}
                {/* Live requirements checklist */}
                {newPassword.length > 0 && (
                  <PasswordRequirements password={newPassword} />
                )}
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-otter-earth-800">
                  Confirm Password
                </Label>
                <PasswordInput
                  id="confirmPassword"
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    // Clear inline error on change
                    if (confirmPasswordError) setConfirmPasswordError(null);
                  }}
                  onBlur={handleConfirmPasswordBlur}
                  disabled={isLoading}
                  className="h-11 md:h-10 border-[#E7D8C8] focus-visible:ring-0 focus-visible:border-2 focus-visible:border-[#EA6A2A]"
                  autoComplete="new-password"
                />
                {/* Inline confirm password error */}
                {confirmPasswordError && (
                  <p className="text-sm text-red-500 mt-1">{confirmPasswordError}</p>
                )}
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-4">
              <Button
                type="submit"
                variant="warm"
                className="w-full rounded-[10px]"
                disabled={isStep2SubmitDisabled}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  'Reset Password'
                )}
              </Button>

              <p className="text-center text-sm text-gray-500">
                Didn't receive the code?{' '}
                <button
                  type="button"
                  onClick={handleResend}
                  className="text-otter-earth-700 hover:text-otter-orange-500 hover:underline"
                >
                  Resend
                </button>
              </p>

              <p className="text-center text-sm text-gray-500">
                <Link
                  to="/login"
                  className="text-otter-earth-700 hover:text-otter-orange-500 hover:underline"
                >
                  Back to Sign In
                </Link>
              </p>
            </CardFooter>
          </form>
        </Card>
      </div>
    );
  }

  // ── Step 1: Email entry ─────────────────────────────────────────────────────

  return (
    <div
      className="flex min-h-screen items-center justify-center px-3 md:px-4 py-8 md:py-12"
      style={{ backgroundColor: '#fdf8f0' }}
    >
      <Card className="w-full max-w-md bg-otter-paper rounded-[20px] shadow-md border-[#E7D8C8]">
        <CardHeader className="space-y-1 text-center">
          <Mail className="mx-auto h-12 w-12 text-otter-orange-500" />
          <CardTitle className="text-2xl font-bold text-otter-earth-800">Reset Your Password</CardTitle>
          <CardDescription className="text-otter-earth-700">
            Enter your email address and we'll send you a reset code.
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleStep1Submit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-otter-earth-800">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={handleEmailBlur}
                disabled={isLoading}
                className="h-11 md:h-10 border-[#E7D8C8]"
                autoComplete="email"
                autoFocus
              />
              {error && (
                <p className="text-sm text-red-500 mt-1">{error}</p>
              )}
            </div>
          </CardContent>

          <CardFooter className="flex flex-col space-y-4">
            <Button
              type="submit"
              variant="warm"
              className="w-full rounded-[10px]"
              disabled={isLoading || !email.trim()}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Code'
              )}
            </Button>

            <p className="text-center text-sm text-gray-500">
              <Link
                to="/login"
                className="text-otter-earth-700 hover:text-otter-orange-500 hover:underline"
              >
                Back to Sign In
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
